import React from 'react'

type Props = {
    children: React.ReactNode
    name?: string
}

type State = {
    error: Error | null
    componentStack: string | null
}

class RouteErrorBoundary extends React.Component<Props, State> {
    state: State = { error: null, componentStack: null }

    static getDerivedStateFromError(error: Error): State {
        return { error, componentStack: null }
    }

    componentDidCatch(error: Error, info: React.ErrorInfo) {
        const tag = `[RouteErrorBoundary${this.props.name ? `:${this.props.name}` : ''}]`
        // Log everything we can — minified prod stacks are nearly useless without context.
        // eslint-disable-next-line no-console
        console.error(tag, error.message, {
            error,
            stack: error.stack,
            componentStack: info.componentStack,
            href: window.location.href,
            userAgent: navigator.userAgent,
            timestamp: new Date().toISOString(),
        })
        this.setState({ componentStack: info.componentStack || null })
    }

    handleReload = () => {
        window.location.reload()
    }

    handleCopy = () => {
        const { error, componentStack } = this.state
        const payload = [
            `URL: ${window.location.href}`,
            `Time: ${new Date().toISOString()}`,
            `Message: ${error?.message ?? ''}`,
            `Stack:\n${error?.stack ?? ''}`,
            `Component stack:${componentStack ?? ''}`,
        ].join('\n\n')
        navigator.clipboard?.writeText(payload).catch(() => undefined)
    }

    render() {
        if (this.state.error) {
            return (
                <div style={{ padding: 24, fontFamily: 'sans-serif', maxWidth: 900 }}>
                    <h2 style={{ marginBottom: 8 }}>Something went wrong loading this page.</h2>
                    <p style={{ color: '#444' }}>
                        The page hit an unexpected error. Please reload. If it keeps happening, copy the diagnostics
                        below and send them to the team.
                    </p>
                    <p style={{ color: '#a00', fontFamily: 'monospace', marginTop: 12 }}>
                        {this.state.error.message}
                    </p>
                    <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
                        <button onClick={this.handleReload}>Reload page</button>
                        <button onClick={this.handleCopy}>Copy diagnostics</button>
                    </div>
                    {this.state.error.stack && (
                        <pre
                            style={{
                                marginTop: 16,
                                padding: 12,
                                background: '#f6f6f6',
                                color: '#333',
                                fontSize: 12,
                                overflow: 'auto',
                                maxHeight: 240,
                            }}
                        >
                            {this.state.error.stack}
                        </pre>
                    )}
                </div>
            )
        }
        return this.props.children
    }
}

export default RouteErrorBoundary
